import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import {
  createBatchPaymentOrder,
  createPaymentOrder,
  fetchPaymentOrderStatus
} from '../../../../actions/payment'
import paymentConfig from '../../../../constants/payment'
import { fetchGroupInfo } from '../../../../actions/group'
import { message, notification } from 'antd'
import { capturePromiseException } from '../../../../utils/sentryUtils'
import { calcCheckedPhotoNum } from '@/pages/components/photoLib/photo'
import {
  trackPaymentSuccess,
  trackPaymentFailure
} from '../utils/sensorTracking'
import { PAYMENT_CONFIG } from '../utils/paymentConfig'

const { channelType, alipayUrlPrefix, paymentStatus } = paymentConfig

export function usePaymentOrder(
  selectedGoods: any,
  options: {
    paySource?: string
    sensorClickEvent?: (evt: string) => void
    onClose?: () => void
    /** 支付成功时的自定义回调；若提供则替代默认的 message.success，由调用方自行提示 */
    onPaymentSuccess?: (orderNo: string) => Promise<void> | void
  } = {}
) {
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state: any) => state.group.groupId)
  const exportDownloadPhotoOnceParams = useAppSelector(
    (state: any) => state.photo.exportDownloadPhotoOnceParams
  )
  // 从全局状态读取 sourceDetail（页面来源细节），以便在创建订单时传递给后端
  const sourceDetail = useAppSelector(
    (state: any) => state.main.paymentStatus?.sourceDetail
  )

  const [alipayUrl, setAlipayUrl] = useState('')
  const [wechatUrl, setWechatUrl] = useState('')
  const [channel, setChannel] = useState(channelType.Alipay)
  const [isCreatingOrder, setIsCreatingOrder] = useState(false)
  const [qrCodeInvalid, setQrCodeInvalid] = useState(false)

  const orderNo = useRef('')
  const orderStatusTimer = useRef<any>(null)
  const notificationTimer = useRef<any>(null)
  const pollingCount = useRef(0)
  const componentUnmounted = useRef(false)
  // 避免短时间内重复发起，后端重复创建订单
  const isCreatingOrderRef = useRef(false)
  const lastCreateRef = useRef<{ goodsId: string; time: number }>({
    goodsId: '',
    time: 0
  })

  const selectPhotosCounts = useMemo(() => {
    try {
      return calcCheckedPhotoNum((window as any).__photoState || {})
    } catch (e) {
      return 0
    }
  }, [])
  // 使用 ref 保存 channel，以便 createOrder 的引用稳定并能读取最新的 channel 值
  const channelRef = useRef(channel)
  useEffect(() => {
    channelRef.current = channel
  }, [channel])

  // 默认以微信支付，如果商品需要支付宝支付，则切换到支付宝支付
  useEffect(() => {
    if (!selectedGoods) return
    const vipType = selectedGoods?.content?.vipType
    const mustAlipay =
      selectedGoods?.type === 2 ||
      vipType === paymentConfig.paymentType.multipleMonth ||
      vipType === paymentConfig.paymentType.multipleYear ||
      vipType === paymentConfig.paymentType.periodQuarter
    const preferred = mustAlipay ? channelType.Alipay : channelType.WeChat
    if (preferred !== channelRef.current) {
      setChannel(preferred)
      channelRef.current = preferred
    }
  }, [selectedGoods])

  // 使用 ref 保存回调和 paySource，使父组件每次渲染传入新函数/对象时不会影响内部引用
  const sensorClickEventRef = useRef(options.sensorClickEvent)
  const onCloseRef = useRef(options.onClose)
  const onPaymentSuccessRef = useRef(options.onPaymentSuccess)
  const paySourceRef = useRef(options.paySource)
  useEffect(() => {
    sensorClickEventRef.current = options.sensorClickEvent
    onCloseRef.current = options.onClose
    onPaymentSuccessRef.current = options.onPaymentSuccess
    paySourceRef.current = options.paySource
  }, [
    options.sensorClickEvent,
    options.onClose,
    options.onPaymentSuccess,
    options.paySource
  ])

  useEffect(() => {
    return () => {
      componentUnmounted.current = true
      clearInterval(orderStatusTimer.current)
      clearInterval(notificationTimer.current)
      notification.destroy()
    }
  }, [])

  const clearTimers = useCallback(() => {
    clearInterval(orderStatusTimer.current)
    orderStatusTimer.current = null
    clearInterval(notificationTimer.current)
    notificationTimer.current = null
    notification.destroy()
  }, [])
  // 设置创建中状态（同时维护 ref 与 state），避免重复请求时刻不一致
  const setCreatingState = useCallback((flag: boolean) => {
    isCreatingOrderRef.current = flag
    setIsCreatingOrder(flag)
  }, [])

  const pollingOrderStatus = useCallback(() => {
    if (orderStatusTimer.current) {
      clearInterval(orderStatusTimer.current)
      orderStatusTimer.current = null
    }
    orderStatusTimer.current = setInterval(() => {
      if (componentUnmounted.current) {
        clearInterval(orderStatusTimer.current)
        return
      }
      pollingCount.current++
      handleFetchOrderStatus()
    }, PAYMENT_CONFIG.POLLING.DELAY)
  }, [])

  const handlePaymentSuccess = useCallback(async () => {
    trackPaymentSuccess(groupID)
    clearTimers()

    // 若调用方提供了自定义支付成功回调，则优先执行（由调用方自行提示用户）
    if (onPaymentSuccessRef.current) {
      try {
        await onPaymentSuccessRef.current(orderNo.current)
      } catch (e) {
        console.error('onPaymentSuccess 回调异常:', e)
      }
    } else {
      message.success(PAYMENT_CONFIG.MESSAGES.PAYMENT_SUCCESS)
    }

    // 刷新团队信息，关闭弹窗
    dispatch(
      fetchGroupInfo({
        group_id: groupID
      })
    )
      .then(() => {
        if (onCloseRef.current) onCloseRef.current()
      })
      .catch(() => {
        if (onCloseRef.current) onCloseRef.current()
      })
  }, [groupID, clearTimers])

  const handleFetchOrderStatus = useCallback(() => {
    if (pollingCount.current >= PAYMENT_CONFIG.POLLING.INVALID_COUNT) {
      setWechatUrl('')
      setAlipayUrl('')
      setQrCodeInvalid(true)
      if (pollingCount.current >= PAYMENT_CONFIG.POLLING.MAX_COUNT) {
        clearInterval(orderStatusTimer.current)
        orderStatusTimer.current = null
      }
    }
    dispatch(
      fetchPaymentOrderStatus({
        orderNo: orderNo.current,
        isBatch: paySourceRef.current === 'singleBatchDownload'
      })
    )
      .then((data: any) => {
        if (data.status === 0) {
          // 简化处理：已支付 -> 触发成功；支付失败 -> 记录并停止；否则继续等待
          if (data.payStatus === paymentStatus.Paid.code) {
            handlePaymentSuccess()
          } else if (data.payStatus === paymentStatus.PaymentFail.code) {
            trackPaymentFailure(groupID)
            clearTimers()
          }
        }
      })
      .catch(capturePromiseException)
  }, [dispatch, handlePaymentSuccess, clearTimers])

  const handleBatchDownloadCreateOrder = useCallback(
    (params: any) => {
      // 并发保护：短时间内重复创建同一商品的订单则忽略
      const now = Date.now()
      const maybeGoodsId = params?.goodsInfo?.[0]?.goodsID || ''
      if (isCreatingOrderRef.current) return
      if (
        maybeGoodsId &&
        lastCreateRef.current.goodsId === maybeGoodsId &&
        now - lastCreateRef.current.time < 1000
      ) {
        return
      }
      setCreatingState(true)
      lastCreateRef.current = { goodsId: maybeGoodsId, time: now }
      params.extra = {
        exportPayload: exportDownloadPhotoOnceParams
      }
      return dispatch(createBatchPaymentOrder(params))
        .then((data: any) => {
          if (data.status === 0) {
            const isWechat = params.channel === channelType.WeChat
            if (isWechat) {
              setWechatUrl(data.wechat.qrcodeUrl)
              setAlipayUrl('')
            } else {
              setAlipayUrl(alipayUrlPrefix + data.alipay.requestBody)
              setWechatUrl('')
            }
            setQrCodeInvalid(false)
            orderNo.current = data.orderNo
            localStorage.setItem('orderNo', data.orderNo)
            pollingOrderStatus()
          } else if (data.status === 9) {
            message.error(PAYMENT_CONFIG.MESSAGES.ORDER_CREATING_BY_OTHERS)
            // status 9 表示已被占用，保持 isCreatingOrder 为 true 以示当前受限状态
            setCreatingState(true)
          } else {
            message.error(data.msg)
            setAlipayUrl('')
            setWechatUrl('')
            setCreatingState(false)
          }
          return data
        })
        .catch((err) => {
          capturePromiseException(err)
          throw err
        })
        .finally(() => {
          setCreatingState(false)
        })
    },
    [dispatch, exportDownloadPhotoOnceParams, pollingOrderStatus]
  )

  // createOrder 使用 channelRef.current，使函数引用稳定
  const createOrder = useCallback(
    (isClickRefresh = false) => {
      const currentChannel = channelRef.current
      if (isClickRefresh) {
        const refresh =
          currentChannel === channelType.Alipay
            ? 'refreshAliQRcode'
            : 'refreshWechatQRcode'
        if (sensorClickEventRef.current) sensorClickEventRef.current(refresh)
      }
      clearTimers()
      pollingCount.current = 0
      if (selectedGoods) {
        const _channel = currentChannel

        // 并发保护：避免短时间内重复为同一商品创建订单
        const now = Date.now()
        if (isCreatingOrderRef.current) return
        if (
          lastCreateRef.current.goodsId === selectedGoods.id &&
          now - lastCreateRef.current.time < 1000
        ) {
          return
        }
        setCreatingState(true)
        lastCreateRef.current = { goodsId: selectedGoods.id, time: now }

        if (paySourceRef.current === 'singleBatchDownload') {
          handleBatchDownloadCreateOrder({
            groupID,
            goodsInfo: [
              {
                goodsID: selectedGoods.id,
                cnt: selectPhotosCounts
              }
            ],
            channel: _channel,
            sourceDetail
          })
          return
        }

        const params: any = {
          goodsID: selectedGoods.id,
          channel: _channel,
          groupID,
          sourceDetail
        }
        return dispatch(createPaymentOrder(params))
          .then((data: any) => {
            if (data.status === 0) {
              const isWechat = _channel === channelType.WeChat
              if (isWechat) {
                setWechatUrl(data.wechat.qrcodeUrl)
                setAlipayUrl('')
              } else {
                setAlipayUrl(alipayUrlPrefix + data.alipay.requestBody)
                setWechatUrl('')
              }
              setQrCodeInvalid(false)
              orderNo.current = data.orderNo
              pollingOrderStatus()
            } else if (data.status === 9) {
              message.error(PAYMENT_CONFIG.MESSAGES.ORDER_CREATING_BY_OTHERS)
              setCreatingState(true)
            } else {
              message.error(data.msg)
              setAlipayUrl('')
              setWechatUrl('')
              setCreatingState(false)
            }
            return data
          })
          .catch((err) => {
            capturePromiseException(err)
            throw err
          })
          .finally(() => {
            setCreatingState(false)
          })
      }
    },
    [
      selectedGoods,
      clearTimers,
      dispatch,
      handleBatchDownloadCreateOrder,
      groupID,
      pollingOrderStatus,
      selectPhotosCounts
    ]
  )

  // 当 selectedGoods 变化时自动创建订单
  useEffect(() => {
    if (selectedGoods) {
      createOrder()
    } else {
      // reset urls
      setAlipayUrl('')
      setWechatUrl('')
      setQrCodeInvalid(false)
    }
  }, [selectedGoods])

  // 当 channel 变化时，如果存在 selectedGoods，则创建新订单（视为刷新）
  useEffect(() => {
    if (selectedGoods) {
      createOrder(true)
    }
  }, [channel])

  return {
    alipayUrl,
    wechatUrl,
    qrCodeInvalid,
    channel,
    setChannel: (c: number) => {
      setChannel(c)
      channelRef.current = c
    },
    isCreatingOrder,
    createOrder,
    clearTimers
  }
}
